import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBook, FaStickyNote, FaGraduationCap, FaComments } from "react-icons/fa";
import { toast } from "react-toastify";
import StudentService from "../../services/studentService";
import ChatService from "../../services/chat.service";
import { useAuth } from "../../context/AuthContext";
import "../../styles/StudentBottomNav.css";

const StudentBottomNav = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [myCourseLink, setMyCourseLink] = useState("/student/courses");
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // Hide on scroll down, show on scroll up.
      // Small buffer (10px) to prevent jitter on tiny scrolls.
      if (currentScrollY > lastScrollY + 10 && currentScrollY > 100) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY - 10) {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    // Only fetch for students
    if (user?.role === "student") {
      fetchEnrolledCourses();
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 10000); // Check every 10s
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const res = await ChatService.getStudentUnreadCount(user.id);
      if (res.data && res.data.status) {
        setUnreadCount(res.data.unread);
      }
    } catch (err) {}
  };

  const fetchEnrolledCourses = async () => {
    try {
      setLoading(true);
      const response = await StudentService.getStudentCourses(user.id);
      const courses = response.data || [];
      
      // If exactly 1 enrolled course, directly link to it
      if (courses.length === 1) {
        setMyCourseLink(`/student/courses/${courses[0].course_id}`);
      } else {
        // 0 or >1 courses goes to courses list
        setMyCourseLink("/student/courses");
      }
    } catch (error) {
      console.error("Error fetching student courses for nav", error);
      // Fallback
      setMyCourseLink("/student/courses");
    } finally {
      setLoading(false);
    }
  };


  // Do not render if not logged in or not a student
  if (!user || user.role !== "student") {
    return null;
  }

  // Helper to determine if a route is active
  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <div 
      className={`student-bottom-nav ${!isVisible ? 'hidden' : ''}`}
      style={{
        transition: 'transform 0.3s ease-in-out',
        transform: isVisible ? 'translateY(0)' : 'translateY(100%)'
      }}
    >
      <Link 
        to={myCourseLink} 
        className={`bottom-nav-item ${isActive("/student/courses") ? "active" : ""}`}
      >
        <FaGraduationCap className="bottom-nav-icon" />
        <span className="bottom-nav-label">My Course</span>
      </Link>

      <Link 
        to="/student/notes" 
        className={`bottom-nav-item ${isActive("/student/notes") ? "active" : ""}`}
      >
        <FaStickyNote className="bottom-nav-icon" />
        <span className="bottom-nav-label">Notes</span>
      </Link>

      <Link 
        to="/student/available" 
        className={`bottom-nav-item ${isActive("/student/available") ? "active" : ""}`}
      >
        <FaBook className="bottom-nav-icon" />
        <span className="bottom-nav-label">All Courses</span>
      </Link>

      <Link 
        to="/student/chat" 
        className={`bottom-nav-item ${isActive("/student/chat") ? "active" : ""}`}
        style={{ position: "relative" }}
      >
        <FaComments className="bottom-nav-icon" />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '5px',
            right: '25px',
            width: '10px',
            height: '10px',
            backgroundColor: 'red',
            borderRadius: '50%',
            display: 'inline-block'
          }}></span>
        )}
        <span className="bottom-nav-label">Chat</span>
      </Link>
    </div>
  );
};

export default StudentBottomNav;
